import React, { useEffect, useState } from "react";
import api from "../lib/api";
import { API } from "../lib/api";
import { toast } from "sonner";
import { DataLabel, LANG_LABEL } from "../components/common";
import {
  Rocket, CheckCircle, Copy, ClockCounterClockwise, CircleNotch, ArrowSquareOut,
  Package, Broadcast, Eye, Lightning, XCircle, FloppyDisk, ImageSquare, Books,
} from "@phosphor-icons/react";

const fmt = (iso) => { if (!iso) return "—"; try { return new Date(iso).toLocaleString("fr-FR", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" }); } catch { return iso; } };

function StatCard({ label, value, sub, accent, testid }) {
  return (
    <div data-testid={testid} className="bg-white border border-border rounded-md p-5">
      <DataLabel>{label}</DataLabel>
      <div className="font-serif text-4xl mt-1.5" style={{ color: accent }}>{value}</div>
      {sub && <div className="text-[11px] text-muted-foreground mt-1">{sub}</div>}
    </div>
  );
}

function FeedRow({ label, url }) {
  const full = url.startsWith("http") ? url : `${API}${url.replace("/api", "")}`;
  const copy = () => { navigator.clipboard.writeText(full); toast.success("Lien copié"); };
  return (
    <div className="flex items-center gap-2 py-2 border-b border-border last:border-0">
      <span className="text-[11px] font-mono uppercase tracking-wide text-muted-foreground w-40 shrink-0">{label}</span>
      <code className="text-[11px] text-ai truncate flex-1">{full}</code>
      <button data-testid={`copy-${label}`} onClick={copy} className="text-muted-foreground hover:text-brand transition-colors"><Copy size={14} /></button>
      <a href={full} target="_blank" rel="noreferrer" className="text-muted-foreground hover:text-brand transition-colors"><ArrowSquareOut size={14} /></a>
    </div>
  );
}

export default function Publication() {
  const [s, setS] = useState(null);
  const [history, setHistory] = useState([]);
  const [deploying, setDeploying] = useState(false);
  const [sd, setSd] = useState({ enabled: false, revalidate_url: "", revalidate_secret: "" });
  const [savingSd, setSavingSd] = useState(false);
  const [testingSd, setTestingSd] = useState(false);
  const [backfilling, setBackfilling] = useState(false);
  const [reviewing, setReviewing] = useState(false);
  const [redocing, setRedocing] = useState(false);

  const load = async () => {
    const [a, b] = await Promise.all([api.get("/deploy/status"), api.get("/deploy/history")]);
    setS(a.data); setHistory(b.data);
    if (a.data.site_deploy) setSd((prev) => ({
      enabled: a.data.site_deploy.enabled, revalidate_url: a.data.site_deploy.revalidate_url || "",
      revalidate_secret: prev.revalidate_secret || (a.data.site_deploy.has_secret ? "••••••••" : ""),
    }));
  };
  useEffect(() => { load(); const t = setInterval(load, 12000); return () => clearInterval(t); }, []);

  const saveSd = async () => {
    setSavingSd(true);
    try {
      const payload = { enabled: sd.enabled, revalidate_url: sd.revalidate_url };
      if (sd.revalidate_secret && sd.revalidate_secret !== "••••••••") payload.revalidate_secret = sd.revalidate_secret;
      await api.put("/strategy", { site_deploy: payload });
      toast.success("Revalidation site enregistrée");
      await load();
    } catch { toast.error("Enregistrement impossible"); }
    finally { setSavingSd(false); }
  };

  const testSd = async () => {
    setTestingSd(true);
    try {
      const { data } = await api.post("/deploy/revalidate-test");
      if (!data.attempted) toast.info("Revalidation non configurée (URL manquante ou désactivée)");
      else if (data.ok) toast.success(`Webhook OK (HTTP ${data.status})`);
      else toast.error(`Échec webhook: ${data.detail || data.status}`);
    } catch { toast.error("Test impossible"); }
    finally { setTestingSd(false); }
  };

  const backfillImages = async () => {
    setBackfilling(true);
    try {
      const { data } = await api.post("/content/backfill-images");
      if (data.started === 0) toast.info("Toutes les images sont déjà générées ✔");
      else toast.success(`${data.started} image(s) en génération…`, { description: "Elles apparaîtront progressivement sur les cartes du site." });
    } catch { toast.error("Génération impossible"); }
    finally { setBackfilling(false); }
  };

  const quarterlyReview = async () => {
    setReviewing(true);
    try {
      await api.post("/agents/quarterly_review/run");
      toast.success("Révision trimestrielle lancée", { description: "Les articles sont passés en revue ; vous recevrez un e-mail récapitulatif. Ceux nécessitant une mise à jour arriveront dans la file de validation." });
    } catch { toast.error("Impossible de lancer la révision"); }
    finally { setReviewing(false); }
  };

  const redocument = async () => {
    setRedocing(true);
    try {
      await api.post("/agents/redocument/run");
      toast.success("Re-documentation lancée", { description: "Les anciens articles peu sourcés sont enrichis de références réelles ; les nouvelles versions arriveront dans la file de validation (~1-2 min/article)." });
    } catch { toast.error("Impossible de lancer la re-documentation"); }
    finally { setRedocing(false); }
  };

  const deploy = async () => {
    if (!s?.pending_count) return;
    if (!window.confirm(`Déployer ${s.pending_count} contenu(s) vers clinicavalorian.es ? Ils seront inclus dans le prochain build du site.`)) return;
    setDeploying(true);
    try {
      const { data } = await api.post("/deploy/trigger");
      const rv = data.revalidation || {};
      const desc = rv.attempted
        ? (rv.ok ? "Site notifié — apparition immédiate sur clinicavalorian.es." : `Webhook de revalidation en échec (${rv.detail || rv.status}). Contenu live dans le feed mais site non notifié.`)
        : "Revalidation site non configurée — contenu live dans le feed.";
      toast.success(`Déploiement v${data.version} — ${data.deployed} contenu(s) mis en ligne`, { description: desc });
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Déploiement impossible");
    } finally { setDeploying(false); }
  };

  if (!s) return <div className="font-mono text-xs text-muted-foreground">Chargement…</div>;

  return (
    <div data-testid="publication-page">
      <div className="flex flex-wrap items-end justify-between gap-3 mb-6">
        <div>
          <div className="text-[11px] font-mono uppercase tracking-[0.25em] text-muted-foreground mb-1">Connecteur site · clinicavalorian.es</div>
          <h1 className="font-serif text-4xl text-brand tracking-tight">Publication & déploiement</h1>
        </div>
        <div className="flex items-center gap-2">
          <button data-testid="redocument-btn" onClick={redocument} disabled={redocing}
            className="flex items-center gap-2 border border-border text-foreground hover:bg-secondary disabled:opacity-50 px-4 py-2.5 rounded-md text-sm font-medium transition-colors"
            title="Enrichit les anciens articles peu sourcés avec de vraies références (PubMed)">
            {redocing ? <CircleNotch size={16} className="ai-pulse" /> : <Books size={16} />}
            Re-documenter les anciens
          </button>
          <button data-testid="quarterly-review-btn" onClick={quarterlyReview} disabled={reviewing}
            className="flex items-center gap-2 border border-border text-foreground hover:bg-secondary disabled:opacity-50 px-4 py-2.5 rounded-md text-sm font-medium transition-colors"
            title="Révision automatique tous les 3 mois — ou lancez-la maintenant si vous avez connaissance d'une nouveauté">
            {reviewing ? <CircleNotch size={16} className="ai-pulse" /> : <ClockCounterClockwise size={16} />}
            Réviser les articles publiés
          </button>
          <button data-testid="backfill-images-btn" onClick={backfillImages} disabled={backfilling}
            className="flex items-center gap-2 border border-brand text-brand hover:bg-brand-soft disabled:opacity-50 px-4 py-2.5 rounded-md text-sm font-medium transition-colors">
            {backfilling ? <CircleNotch size={16} className="ai-pulse" /> : <ImageSquare size={16} />}
            Générer les images manquantes
          </button>
          <button data-testid="deploy-trigger-btn" onClick={deploy} disabled={deploying || !s.pending_count}
            className="flex items-center gap-2 bg-brand text-white hover:bg-brand-hover disabled:opacity-50 px-5 py-2.5 rounded-md text-sm font-medium transition-colors">
            {deploying ? <CircleNotch size={16} className="ai-pulse" /> : <Rocket size={16} weight="fill" />}
            Déclencher le déploiement{s.pending_count ? ` (${s.pending_count})` : ""}
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-8">
        <StatCard testid="stat-pending" label="Prêts à déployer" value={s.pending_count} accent="#D95D39" sub="Validés, en attente de mise en ligne" />
        <StatCard testid="stat-live" label="En ligne" value={s.live_count} accent="#166534" sub="Servis par le feed de production" />
        <StatCard testid="stat-last" label="Dernier déploiement"
          value={s.last_deployment ? `v${s.last_deployment.version}` : "—"} accent="#0B3B2D"
          sub={s.last_deployment ? fmt(s.last_deployment.created_at) : "Aucun déploiement"} />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Ready to deploy */}
        <div className="lg:col-span-2">
          <div className="flex items-center gap-2 mb-3"><Package size={16} className="text-warn" /><DataLabel>Contenus prêts à déployer</DataLabel></div>
          <div className="bg-white border border-border rounded-md divide-y divide-border" data-testid="pending-list">
            {s.pending_items.length === 0 && (
              <div className="p-8 text-center">
                <CheckCircle size={28} weight="fill" className="mx-auto text-emerald-500 mb-2" />
                <p className="text-sm text-muted-foreground">Tout est déployé. Le site est à jour avec vos validations.</p>
              </div>
            )}
            {s.pending_items.map((it) => (
              <div key={it.id} data-testid={`pending-${it.id}`} className="flex items-center gap-3 px-4 py-3">
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-medium leading-snug line-clamp-1">{it.title}</div>
                  <div className="flex items-center gap-2 mt-1 flex-wrap">
                    <span className="text-[10px] font-mono px-1.5 py-0.5 rounded bg-brand-soft text-brand">{it.collection_label}</span>
                    <span className="text-[10px] font-mono text-muted-foreground">{it.type}</span>
                    <span className="flex items-center gap-1">
                      {it.langs.map((l) => <span key={l} className="text-[9px] font-mono uppercase px-1 py-0.5 rounded bg-secondary text-muted-foreground" title={LANG_LABEL[l]}>{l}</span>)}
                    </span>
                  </div>
                </div>
                <a data-testid={`preview-${it.id}`} href={`/apercu/${it.id}`} target="_blank" rel="noreferrer"
                  className="flex items-center gap-1 text-[11px] font-mono text-ai hover:underline shrink-0" title="Voir l'aperçu web rendu">
                  <Eye size={13} /> Aperçu web
                </a>
              </div>
            ))}
          </div>

          {/* History */}
          <div className="flex items-center gap-2 mt-8 mb-3"><ClockCounterClockwise size={16} className="text-brand" /><DataLabel>Historique des déploiements</DataLabel></div>
          <div className="bg-white border border-border rounded-md divide-y divide-border" data-testid="deploy-history">
            {history.length === 0 && <div className="p-4 text-sm text-muted-foreground">Aucun déploiement pour le moment.</div>}
            {history.map((h) => (
              <div key={h.id} className="flex items-center gap-3 px-4 py-3">
                <div className="w-9 h-9 rounded-md bg-brand-soft text-brand flex items-center justify-center font-serif text-sm shrink-0">v{h.version}</div>
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-medium">{h.deployed_now} contenu(s) mis en ligne · {h.total_live} au total</div>
                  <div className="text-[11px] text-muted-foreground">{fmt(h.created_at)} · {h.triggered_by}</div>
                  {h.verification && (
                    <div className="text-[11px] mt-0.5" style={{ color: h.verification.verified === h.verification.total ? "#166534" : "#D95D39" }}>
                      {h.verification.verified === h.verification.total
                        ? `✓ ${h.verification.verified}/${h.verification.total} vérifié(s) en ligne sur le site`
                        : `${h.verification.verified}/${h.verification.total} visible(s) sur le site — le reste attend le branchement`}
                    </div>
                  )}
                </div>
                <div className="flex gap-1 flex-wrap justify-end">
                  {Object.entries(h.counts_by_collection || {}).map(([k, v]) => (
                    <span key={k} className="text-[10px] font-mono px-1.5 py-0.5 rounded bg-secondary text-muted-foreground">{k}: {v}</span>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Integration info */}
        <div>
          <div className="flex items-center gap-2 mb-3"><Broadcast size={16} className="text-ai" /><DataLabel>Contrat du connecteur</DataLabel></div>
          <div className="bg-white border border-border rounded-md p-4">
            <p className="text-xs text-muted-foreground mb-3">Le site clinicavalorian.es consomme ces endpoints au moment du build pour créer/mettre à jour ses pages (collections, URL, méta, JSON-LD, hreflang, maillage, traductions).</p>
            <FeedRow label="feed production" url={s.feed_urls.feed} />
            <FeedRow label="aperçu (staging)" url={s.feed_urls.preview} />
            <FeedRow label="détail contenu" url={s.feed_urls.content_detail} />
            <FeedRow label="sitemap" url={s.feed_urls.sitemap} />
            <FeedRow label="manifeste" url={s.feed_urls.deployment_manifest} />
            {s.feed_urls.health && <FeedRow label="santé (health)" url={s.feed_urls.health} />}
          </div>
          <div className="bg-brand-soft border border-brand/20 rounded-md p-4 mt-4">
            <div className="text-xs font-medium text-brand mb-1">Workflow (semi-automatique)</div>
            <ol className="text-[11px] text-brand/80 space-y-1 list-decimal list-inside">
              <li>L'IA génère → vous recevez un e-mail</li>
              <li>Vous validez dans la file de validation</li>
              <li>Le contenu apparaît ici, prêt à déployer</li>
              <li>Vous cliquez « Déclencher le déploiement »</li>
              <li>Apparition immédiate sur le site (si revalidation activée)</li>
            </ol>
          </div>

          {/* Instant revalidation config */}
          <div className="flex items-center gap-2 mt-6 mb-3"><Lightning size={16} weight="fill" className="text-warn" /><DataLabel>Revalidation instantanée du site</DataLabel></div>
          <div className="bg-white border border-border rounded-md p-4" data-testid="revalidate-config">
            <p className="text-xs text-muted-foreground mb-3">Endpoint <code className="text-ai">/api/revalidate</code> du site (mode dynamique) appelé au déploiement pour une mise en ligne immédiate, sans Publish manuel.</p>
            <label className="flex items-center gap-2 mb-3 cursor-pointer">
              <input type="checkbox" data-testid="revalidate-enabled" checked={sd.enabled}
                onChange={(e) => setSd({ ...sd, enabled: e.target.checked })} className="accent-brand" />
              <span className="text-xs">Activer la revalidation au déploiement</span>
            </label>
            <input data-testid="revalidate-url" value={sd.revalidate_url} placeholder="https://clinicavalorian.es/api/revalidate"
              onChange={(e) => setSd({ ...sd, revalidate_url: e.target.value })}
              className="w-full text-xs border border-border rounded-md px-2.5 py-2 mb-2 font-mono focus:outline-none focus:border-brand" />
            <input data-testid="revalidate-secret" type="password" value={sd.revalidate_secret} placeholder="Secret partagé"
              onChange={(e) => setSd({ ...sd, revalidate_secret: e.target.value })}
              className="w-full text-xs border border-border rounded-md px-2.5 py-2 mb-3 font-mono focus:outline-none focus:border-brand" />
            <div className="flex items-center gap-2">
              <button data-testid="revalidate-save" onClick={saveSd} disabled={savingSd}
                className="flex items-center gap-1.5 text-xs bg-brand text-white hover:bg-brand-hover disabled:opacity-60 px-3 py-1.5 rounded-md transition-colors">
                {savingSd ? <CircleNotch size={13} className="ai-pulse" /> : <FloppyDisk size={13} />} Enregistrer
              </button>
              <button data-testid="revalidate-test" onClick={testSd} disabled={testingSd}
                className="flex items-center gap-1.5 text-xs border border-brand text-brand hover:bg-brand-soft disabled:opacity-60 px-3 py-1.5 rounded-md transition-colors">
                {testingSd ? <CircleNotch size={13} className="ai-pulse" /> : <Lightning size={13} />} Tester le webhook
              </button>
            </div>
            {s.last_deployment?.revalidation?.attempted && (
              <div className="mt-3 flex items-center gap-1.5 text-[11px]">
                {s.last_deployment.revalidation.ok
                  ? <><CheckCircle size={13} weight="fill" className="text-emerald-600" /><span className="text-emerald-700">Dernier appel OK (HTTP {s.last_deployment.revalidation.status})</span></>
                  : <><XCircle size={13} weight="fill" className="text-red-500" /><span className="text-red-600">Dernier appel en échec ({s.last_deployment.revalidation.detail || s.last_deployment.revalidation.status})</span></>}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
